import type { RouteRecordRaw } from 'vue-router'

// Inventory module routes. Mounted under /MOPLayout in router/unified-routes.ts.
// One masters home + one route per master. Add new masters here, keeping the
// path kebab-case and the name prefixed MOPInv* for uniqueness.
export const inventoryRoutes: RouteRecordRaw[] = [
  {
    path: 'inventory',
    // Inventory layout renders the module action bar (store selector, work list,
    // notifications, code template, apps, language, profile) across all inventory routes.
    component: () => import('@/modules/Inventory/components/Layout/InventoryLayout.vue'),
    meta: { title: 'Inventory', requiresAuth: true },
    children: [
      {
        path: '',
        redirect: { name: 'MOPInvMasters' }
      },
      // Masters landing (grid of master cards) — menu-driven, sets EntityData.EntityId on click.
      {
        path: 'masters',
        name: 'MOPInvMasters',
        component: () => import('@/modules/Inventory/masters/components/MastersHome.vue'),
        meta: { title: 'Inventory Masters', requiresAuth: true, breadcrumb: 'Masters' }
      },

      // --- Masters ---
      {
        path: 'masters/unit-master',
        name: 'MOPInvUnitMaster',
        component: () => import('@/modules/Inventory/masters/unitMaster/components/UnitMasterList.vue'),
        meta: { title: 'Unit Master', requiresAuth: true, breadcrumb: 'Unit Master' }
      },
      {
        path: 'masters/sub-tax-master',
        name: 'MOPInvSubTaxMaster',
        component: () => import('@/modules/Inventory/masters/subTaxMaster/components/SubTaxMasterList.vue'),
        meta: { title: 'Sub Tax Master', requiresAuth: true, breadcrumb: 'Sub Tax Master' }
      },
      {
        path: 'masters/tax-master',
        name: 'MOPInvTaxMaster',
        component: () => import('@/modules/Inventory/masters/taxMaster/components/TaxMasterList.vue'),
        meta: { title: 'Tax Master', requiresAuth: true, breadcrumb: 'Tax Master' }
      },
      {
        path: 'masters/hsn-master',
        name: 'MOPInvHsnMaster',
        component: () => import('@/modules/Inventory/masters/hsnMaster/components/HsnMasterList.vue'),
        meta: { title: 'HSN Master', requiresAuth: true, breadcrumb: 'HSN Master' }
      },
      {
        path: 'masters/payment-terms',
        name: 'MOPInvPaymentTerms',
        component: () => import('@/modules/Inventory/masters/paymentTermsMaster/components/PaymentTermsMasterList.vue'),
        meta: { title: 'Payment Terms', requiresAuth: true, breadcrumb: 'Payment Terms' }
      },
      {
        path: 'masters/delivery-terms',
        name: 'MOPInvDeliveryTerms',
        component: () => import('@/modules/Inventory/masters/deliveryTermsMaster/components/DeliveryTermsMasterList.vue'),
        meta: { title: 'Delivery Terms', requiresAuth: true, breadcrumb: 'Delivery Terms' }
      },
      {
        path: 'masters/delivery-mode',
        name: 'MOPInvDeliveryMode',
        component: () => import('@/modules/Inventory/masters/deliveryModeMaster/components/DeliveryModeMasterList.vue'),
        meta: { title: 'Delivery Mode', requiresAuth: true, breadcrumb: 'Delivery Mode' }
      },
      {
        path: 'masters/warranty-terms',
        name: 'MOPInvWarrantyTerms',
        component: () => import('@/modules/Inventory/masters/warrantyTermsMaster/components/WarrantyTermsMasterList.vue'),
        meta: { title: 'Warranty Terms', requiresAuth: true, breadcrumb: 'Warranty Terms' }
      },
      {
        path: 'masters/supplier',
        name: 'MOPInvSupplier',
        component: () => import('@/modules/Inventory/masters/supplierMaster/components/SupplierMasterList.vue'),
        meta: { title: 'Supplier', requiresAuth: true, breadcrumb: 'Supplier' }
      },
      {
        // Full-page create/edit form (MoceroUI routes Supplier to its own page, not a dialog).
        path: 'masters/supplier/create',
        name: 'MOPInvSupplierForm',
        component: () => import('@/modules/Inventory/masters/supplierMaster/components/SupplierMasterForm.vue'),
        meta: { title: 'Supplier', requiresAuth: true, breadcrumb: 'Supplier' }
      },
      {
        // Read-only supplier details (MoceroUI SupplierMasterDetailsView → row click).
        path: 'masters/supplier/details',
        name: 'MOPInvSupplierDetails',
        component: () => import('@/modules/Inventory/masters/supplierMaster/components/SupplierMasterDetails.vue'),
        meta: { title: 'Supplier Details', requiresAuth: true, breadcrumb: 'Supplier Details' }
      },
      {
        path: 'masters/customer',
        name: 'MOPInvCustomer',
        component: () => import('@/modules/Inventory/masters/customerMaster/components/CustomerMasterList.vue'),
        meta: { title: 'Customer', requiresAuth: true, breadcrumb: 'Customer' }
      },
      {
        // Full-page create/edit form (MoceroUI routes Customer to its own page, not a dialog).
        path: 'masters/customer/create',
        name: 'MOPInvCustomerForm',
        component: () => import('@/modules/Inventory/masters/customerMaster/components/CustomerMasterForm.vue'),
        meta: { title: 'Customer', requiresAuth: true, breadcrumb: 'Customer' }
      },
      {
        path: 'masters/manufacturer',
        name: 'MOPInvManufacturer',
        component: () => import('@/modules/Inventory/masters/manufacturerMaster/components/ManufacturerMasterList.vue'),
        meta: { title: 'Manufacturer', requiresAuth: true, breadcrumb: 'Manufacturer' }
      },
      {
        // Full-page create/edit form (MoceroUI routes Manufacturer to its own page).
        path: 'masters/manufacturer/create',
        name: 'MOPInvManufacturerForm',
        component: () => import('@/modules/Inventory/masters/manufacturerMaster/components/ManufacturerMasterForm.vue'),
        meta: { title: 'Manufacturer', requiresAuth: true, breadcrumb: 'Manufacturer' }
      },
      {
        // Read-only details (MoceroUI ManufacturerMasterDetails) — opened by row-click.
        path: 'masters/manufacturer/details',
        name: 'MOPInvManufacturerDetails',
        component: () => import('@/modules/Inventory/masters/manufacturerMaster/components/ManufacturerMasterDetails.vue'),
        meta: { title: 'Manufacturer Details', requiresAuth: true, breadcrumb: 'Manufacturer Details' }
      },
      {
        path: 'masters/generic-master',
        name: 'MOPInvGenericMaster',
        component: () => import('@/modules/Inventory/masters/genericMaster/components/GenericMasterList.vue'),
        meta: { title: 'Generic', requiresAuth: true, breadcrumb: 'Generic' }
      },
      {
        path: 'masters/store',
        name: 'MOPInvStore',
        component: () => import('@/modules/Inventory/masters/storeMaster/components/StoreMasterList.vue'),
        meta: { title: 'Store', requiresAuth: true, breadcrumb: 'Store' }
      },
      {
        // Full-page create/edit form (MoceroUI routes Store to its own tabbed page).
        path: 'masters/store/create',
        name: 'MOPInvStoreForm',
        component: () => import('@/modules/Inventory/masters/storeMaster/components/StoreMasterForm.vue'),
        meta: { title: 'Store', requiresAuth: true, breadcrumb: 'Store' }
      },
      {
        // Read-only store details (MoceroUI StoreMasterDetails — opened on row click).
        path: 'masters/store/details',
        name: 'MOPInvStoreDetails',
        component: () => import('@/modules/Inventory/masters/storeMaster/components/StoreMasterDetails.vue'),
        meta: { title: 'Store Details', requiresAuth: true, breadcrumb: 'Store Details' }
      },
      {
        path: 'masters/room',
        name: 'MOPInvRoom',
        component: () => import('@/modules/Inventory/masters/roomMaster/components/RoomMasterList.vue'),
        meta: { title: 'Room', requiresAuth: true, breadcrumb: 'Room' }
      },
      {
        path: 'masters/rack',
        name: 'MOPInvRack',
        component: () => import('@/modules/Inventory/masters/rackMaster/components/RackMasterList.vue'),
        meta: { title: 'Rack', requiresAuth: true, breadcrumb: 'Rack' }
      },
      {
        path: 'masters/row',
        name: 'MOPInvRow',
        component: () => import('@/modules/Inventory/masters/rowMaster/components/RowMasterList.vue'),
        meta: { title: 'Row', requiresAuth: true, breadcrumb: 'Row' }
      },
      {
        path: 'masters/box',
        name: 'MOPInvBox',
        component: () => import('@/modules/Inventory/masters/boxMaster/components/BoxMasterList.vue'),
        meta: { title: 'Box', requiresAuth: true, breadcrumb: 'Box' }
      },
      {
        path: 'masters/category',
        name: 'MOPInvCategory',
        component: () => import('@/modules/Inventory/masters/categoryMaster/components/CategoryMasterList.vue'),
        meta: { title: 'Category', requiresAuth: true, breadcrumb: 'Category' }
      },
      {
        path: 'masters/group',
        name: 'MOPInvGroup',
        component: () => import('@/modules/Inventory/masters/groupMaster/components/GroupMasterList.vue'),
        meta: { title: 'Group', requiresAuth: true, breadcrumb: 'Group' }
      },
      {
        path: 'masters/sub-group',
        name: 'MOPInvSubGroup',
        component: () => import('@/modules/Inventory/masters/subGroupMaster/components/SubGroupMasterList.vue'),
        meta: { title: 'Sub-Group', requiresAuth: true, breadcrumb: 'Sub-Group' }
      },
      {
        path: 'masters/category-supplier-mapping',
        name: 'MOPInvCategorySupplierMapping',
        component: () => import('@/modules/Inventory/masters/categorySupplierMapping/components/CategorySupplierMappingList.vue'),
        meta: { title: 'Category Supplier Mapping', requiresAuth: true, breadcrumb: 'Category Supplier Mapping' }
      },
      {
        path: 'masters/store-category-mapping',
        name: 'MOPInvStoreCategoryMapping',
        component: () => import('@/modules/Inventory/masters/storeCategoryMapping/components/StoreCategoryMappingList.vue'),
        meta: { title: 'Store Category Mapping', requiresAuth: true, breadcrumb: 'Store Category Mapping' }
      },
      {
        // Store - Category - Item Mapping (MoceroUI: storeItemMappingTable, FixedHref "/storeItemMappingTable").
        path: 'masters/store-category-item-mapping',
        name: 'MOPInvStoreCategoryItemMapping',
        component: () => import('@/modules/Inventory/masters/storeCategoryItemMapping/components/StoreCategoryItemMappingList.vue'),
        meta: { title: 'Store - Category - Item Mapping', requiresAuth: true, breadcrumb: 'Store - Category - Item Mapping' }
      },
      {
        // Full-page create/edit: pick Store + Categories, tick items in a table with per-row
        // Room→Rack→Row→Box + re-order levels, save all in bulk.
        path: 'masters/store-category-item-mapping/create',
        name: 'MOPInvStoreCategoryItemMappingForm',
        component: () => import('@/modules/Inventory/masters/storeCategoryItemMapping/components/StoreCategoryItemMappingForm.vue'),
        meta: { title: 'Store - Category - Item Mapping', requiresAuth: true, breadcrumb: 'Store - Category - Item Mapping' }
      },
      {
        // Stock Unit Conversion (MoceroUI: UnitConversionTable, FixedHref "/UnitConversion").
        path: 'masters/unit-conversion',
        name: 'MOPInvUnitConversion',
        component: () => import('@/modules/Inventory/masters/unitConversionMaster/components/UnitConversionList.vue'),
        meta: { title: 'Unit Conversion', requiresAuth: true, breadcrumb: 'Unit Conversion' }
      },
      {
        // Item Configuration (MoceroUI: ItemConfigTable, FixedHref "/ItemConfiguration").
        path: 'masters/item-configuration',
        name: 'MOPInvItemConfiguration',
        component: () => import('@/modules/Inventory/masters/itemConfigurationMaster/components/ItemConfigurationList.vue'),
        meta: { title: 'Item Configuration', requiresAuth: true, breadcrumb: 'Item Configuration' }
      },
      {
        // Stock Reasons (MoceroUI: StockReasonTableview, FixedHref "/StockReasons").
        path: 'masters/stock-reasons',
        name: 'MOPInvStockReasons',
        component: () => import('@/modules/Inventory/masters/stockReasonMaster/components/StockReasonList.vue'),
        meta: { title: 'Stock Reasons', requiresAuth: true, breadcrumb: 'Stock Reasons' }
      },
      {
        // Account Sub Head (MoceroUI: accountSubHeadTableview, FixedHref "/accountSubHeadTableview").
        path: 'masters/account-sub-head',
        name: 'MOPInvAccountSubHead',
        component: () => import('@/modules/Inventory/masters/accountSubHeadMaster/components/AccountSubHeadList.vue'),
        meta: { title: 'Account Sub Head', requiresAuth: true, breadcrumb: 'Account Sub Head' }
      },
      {
        // Account Head (MoceroUI: accountHeadTableview, FixedHref "/accountHeadTableview").
        path: 'masters/account-head',
        name: 'MOPInvAccountHead',
        component: () => import('@/modules/Inventory/masters/accountHeadMaster/components/AccountHeadList.vue'),
        meta: { title: 'Account Head', requiresAuth: true, breadcrumb: 'Account Head' }
      },
      {
        // Surgery Brand Model Mapping (MoceroUI: SurgertBrandModelMappingTable, FixedHref "/SurgertBrandModelMappingTable").
        path: 'masters/surgery-brand-model-mapping',
        name: 'MOPInvSurgeryBrandModelMapping',
        component: () => import('@/modules/Inventory/masters/surgeryBrandModelMaster/components/SurgeryBrandModelList.vue'),
        meta: { title: 'Surgery Brand Model Mapping', requiresAuth: true, breadcrumb: 'Surgery Brand Model Mapping' }
      },
      {
        // Stock Item / Item Master (MoceroUI: ItemTableview, FixedHref "/ItemMasterTableview").
        path: 'masters/stock-item',
        name: 'MOPInvStockItem',
        component: () => import('@/modules/Inventory/masters/stockItemMaster/components/StockItemList.vue'),
        meta: { title: 'Stock Item', requiresAuth: true, breadcrumb: 'Stock Item' }
      },
      {
        // Full-page create/edit form (all sections: details/pricing, grouping, units, config).
        path: 'masters/stock-item/create',
        name: 'MOPInvStockItemForm',
        component: () => import('@/modules/Inventory/masters/stockItemMaster/components/StockItemForm.vue'),
        meta: { title: 'Stock Item', requiresAuth: true, breadcrumb: 'Stock Item' }
      },
      {
        // Read-only details (MoceroUI ItemDetails): 5 tabs — Details/Stores/Suppliers/Documents/Logs.
        path: 'masters/stock-item/details',
        name: 'MOPInvStockItemDetails',
        component: () => import('@/modules/Inventory/masters/stockItemMaster/components/StockItemDetails.vue'),
        meta: { title: 'Stock Item Details', requiresAuth: true, breadcrumb: 'Stock Item Details' }
      },
      {
        // Stock Schedule (MoceroUI: ScheduleMasterTableView, FixedHref "/ScheduleMasterTableView").
        path: 'masters/stock-schedule',
        name: 'MOPInvStockSchedule',
        component: () => import('@/modules/Inventory/masters/stockScheduleMaster/components/StockScheduleList.vue'),
        meta: { title: 'Stock Schedule', requiresAuth: true, breadcrumb: 'Stock Schedule' }
      },
      {
        // Item Onboarding — bulk Excel upload wizard (MoceroUI: Onboarding, FixedHref "/Onboarding").
        path: 'masters/stock-onboarding',
        name: 'MOPInvStockOnboarding',
        component: () => import('@/modules/Inventory/masters/stockOnboardingMaster/components/StockOnboarding.vue'),
        meta: { title: 'Item Onboarding', requiresAuth: true, breadcrumb: 'Item Onboarding' }
      },
      {
        // Item Request (menu "Item Request", FixedHref "ItemRequestTableview", EntityId 3187).
        path: 'masters/item-request',
        name: 'MOPInvItemRequest',
        component: () => import('@/modules/Inventory/masters/itemRequestMaster/components/ItemRequestList.vue'),
        meta: { title: 'Item Request', requiresAuth: true, breadcrumb: 'Item Request' }
      },
      {
        // Full-page request form (item fields + flags + approval, posts to ItemRequest/Create).
        path: 'masters/item-request/create',
        name: 'MOPInvItemRequestForm',
        component: () => import('@/modules/Inventory/masters/itemRequestMaster/components/ItemRequestForm.vue'),
        meta: { title: 'Item Request', requiresAuth: true, breadcrumb: 'Item Request' }
      },
      {
        // Agreement Master (MoceroUI: AssetManagement/agreementMasterTableview).
        path: 'masters/agreement',
        name: 'MOPInvAgreement',
        component: () => import('@/modules/Inventory/masters/agreementMaster/components/AgreementList.vue'),
        meta: { title: 'Agreement Master', requiresAuth: true, breadcrumb: 'Agreement Master' }
      },
      {
        // Full-page create/edit form (asset autocomplete + agreement type/dates/provider/status).
        path: 'masters/agreement/details',
        name: 'MOPInvAgreementDetails',
        component: () => import('@/modules/Inventory/masters/agreementMaster/components/AgreementDetails.vue'),
        meta: { title: 'Agreement Details', requiresAuth: true, breadcrumb: 'Agreement Details' }
      },
      {
        path: 'masters/agreement/create',
        name: 'MOPInvAgreementForm',
        component: () => import('@/modules/Inventory/masters/agreementMaster/components/AgreementForm.vue'),
        meta: { title: 'Agreement Master', requiresAuth: true, breadcrumb: 'Agreement Master' }
      },
      {
        // Asset Status Master (MoceroUI: AssetManagement/assetStatusMasterTableview).
        path: 'masters/asset-status',
        name: 'MOPInvAssetStatus',
        component: () => import('@/modules/Inventory/masters/assetStatusMaster/components/AssetStatusList.vue'),
        meta: { title: 'Asset Status', requiresAuth: true, breadcrumb: 'Asset Status' }
      },
      {
        // Depreciation Method Master (MoceroUI: AssetManagement/depreciationMethodMasterTableview).
        path: 'masters/depreciation-method',
        name: 'MOPInvDepreciationMethod',
        component: () => import('@/modules/Inventory/masters/depreciationMethodMaster/components/DepreciationMethodList.vue'),
        meta: { title: 'Depreciation Method', requiresAuth: true, breadcrumb: 'Depreciation Method' }
      },
      {
        // Depreciation Run — review a period's charge before Accounts posts it.
        // The component lives in the AssetManagement module; it is routed here because
        // that module's own AssetRoutes are not mounted anywhere, and every other asset
        // screen is reached through the Inventory router.
        path: 'transactions/depreciation-run',
        name: 'MOPInvDepreciationRun',
        component: () => import('@/modules/AssetManagement/depreciationRun/components/DepreciationRunView.vue'),
        meta: { title: 'Depreciation Run', requiresAuth: true, breadcrumb: 'Depreciation Run' }
      },
      {
        // Maintenance Schedule Master (MoceroUI: AssetManagement/maintenanceScheduleMasterTableview).
        path: 'masters/maintenance-schedule',
        name: 'MOPInvMaintenanceSchedule',
        component: () => import('@/modules/Inventory/masters/maintenanceScheduleMaster/components/MaintenanceScheduleList.vue'),
        meta: { title: 'Maintenance Schedule', requiresAuth: true, breadcrumb: 'Maintenance Schedule' }
      },
      {
        // Asset CheckList Master (MoceroUI: Masters/Items/Checklist/ChecklistMasterTableView).
        path: 'masters/asset-checklist',
        name: 'MOPInvAssetCheckList',
        component: () => import('@/modules/Inventory/masters/assetCheckListMaster/components/AssetCheckListList.vue'),
        meta: { title: 'Asset CheckList', requiresAuth: true, breadcrumb: 'Asset CheckList' }
      },
      {
        // CSSD Instrument Master (MoceroUI: CSSD/Masters/InstrumentMaster) — list.
        path: 'masters/cssd-instrument',
        name: 'MOPInvCSSDInstrument',
        component: () => import('@/modules/Inventory/masters/cssdInstrumentMaster/components/CSSDInstrumentList.vue'),
        meta: { title: 'CSSD Instrument', requiresAuth: true, breadcrumb: 'CSSD Instrument' }
      },
      {
        // CSSD Instrument Master — create / edit form.
        path: 'masters/cssd-instrument/create',
        name: 'MOPInvCSSDInstrumentForm',
        component: () => import('@/modules/Inventory/masters/cssdInstrumentMaster/components/CSSDInstrumentForm.vue'),
        meta: { title: 'CSSD Instrument', requiresAuth: true, breadcrumb: 'CSSD Instrument' }
      },
      {
        // Sterilization Method Master (MoceroUI: CSSD/Masters/SterilizationMethod) — list.
        path: 'masters/sterilization-method',
        name: 'MOPInvSterilizationMethod',
        component: () => import('@/modules/Inventory/masters/sterilizationMethodMaster/components/SterilizationMethodList.vue'),
        meta: { title: 'Sterilization Method', requiresAuth: true, breadcrumb: 'Sterilization Method' }
      },
      {
        // Sterilization Method Master — create / edit form.
        path: 'masters/sterilization-method/create',
        name: 'MOPInvSterilizationMethodForm',
        component: () => import('@/modules/Inventory/masters/sterilizationMethodMaster/components/SterilizationMethodForm.vue'),
        meta: { title: 'Sterilization Method', requiresAuth: true, breadcrumb: 'Sterilization Method' }
      },
      {
        // CSSD Kit Master (MoceroUI: CSSD/Masters/KitMaster) — list.
        path: 'masters/cssd-kit',
        name: 'MOPInvCSSDKit',
        component: () => import('@/modules/Inventory/masters/cssdKitMaster/components/CSSDKitList.vue'),
        meta: { title: 'CSSD Kit', requiresAuth: true, breadcrumb: 'CSSD Kit' }
      },
      {
        // CSSD Kit Master — create / edit form.
        path: 'masters/cssd-kit/create',
        name: 'MOPInvCSSDKitForm',
        component: () => import('@/modules/Inventory/masters/cssdKitMaster/components/CSSDKitForm.vue'),
        meta: { title: 'CSSD Kit', requiresAuth: true, breadcrumb: 'CSSD Kit' }
      },
      // NEXT MASTERS go here — copy the block above, swap path/name/component.

      // --- Store Details (tabbed dashboard shown when a store is selected in the header) ---
      {
        // Menu-driven tabs (Type=2) for the selected store. MoceroUI parity: StoreTabDetails.
        path: 'store-details',
        name: 'MOPInvStoreTabDetails',
        component: () => import('@/modules/Inventory/storeDetails/components/StoreTabDetails.vue'),
        meta: { title: 'Store Details', requiresAuth: true, breadcrumb: 'Store Details' }
      },

      // --- Transactions ---
      {
        // Purchase Order creation (Quick Create "Create PO" + PO tab). MoceroUI: POCreation.
        path: 'po-creation',
        name: 'MOPInvPOCreation',
        component: () => import('@/modules/Inventory/transactions/purchaseOrder/components/POCreation.vue'),
        meta: { title: 'Raise a PO', requiresAuth: true, breadcrumb: 'Raise a PO' }
      },
      {
        // Purchase Request creation (PR tab "+ Add New"). MoceroUI: purchaseRequestCreation.
        path: 'pr-creation',
        name: 'MOPInvPRCreation',
        component: () => import('@/modules/Inventory/transactions/purchaseRequest/components/PRCreation.vue'),
        meta: { title: 'Raise a PR', requiresAuth: true, breadcrumb: 'Raise a PR' }
      },
      {
        // Rate Contract creation (Rate Contract tab "+ Add New" / Edit). MoceroUI: RateContractCreation.
        path: 'rate-contract-creation',
        name: 'MOPInvRateContractCreation',
        component: () => import('@/modules/Inventory/transactions/rateContract/components/RateContractCreation.vue'),
        meta: { title: 'Rate Contract', requiresAuth: true, breadcrumb: 'Rate Contract' }
      },
      {
        // Supplier Payment detail/approval (Payment tab eye icon). MoceroUI: SupplierPaymentDetails.
        path: 'supplier-payment-details',
        name: 'MOPInvSupplierPaymentDetails',
        component: () => import('@/modules/Inventory/transactions/supplierPayment/components/SupplierPaymentDetails.vue'),
        meta: { title: 'Supplier Payment', requiresAuth: true, breadcrumb: 'Supplier Payment' }
      },
      {
        // Record Consumption (Quick Create "Record Consumption"). MoceroUI: Consumption.
        path: 'consumption',
        name: 'MOPInvConsumption',
        component: () => import('@/modules/Inventory/transactions/consumption/components/ConsumptionCreation.vue'),
        meta: { title: 'Consumption', requiresAuth: true, breadcrumb: 'Consumption' }
      },
      {
        // Replacement request raised against a consumption (MoceroUI: ReplacementRequest, EntityId 3049).
        path: 'replacement-request',
        name: 'MOPInvReplacementRequest',
        component: () => import('@/modules/Inventory/transactions/consumption/components/ReplacementRequest.vue'),
        meta: { title: 'Replacement Request', requiresAuth: true, breadcrumb: 'Replacement Request' }
      },
      {
        // Check Reorder (Quick Create "Check Reorder"). MoceroUI: reOrderLevelBasedPO.
        path: 'reorder',
        name: 'MOPInvReorder',
        component: () => import('@/modules/Inventory/transactions/reorder/components/ReorderLevelPO.vue'),
        meta: { title: 'Raise PO based on Reorder Level', requiresAuth: true, breadcrumb: 'Raise PO based on Reorder Level' }
      },
      {
        // Consignee PO (Quick Create "Consignee PO"). MoceroUI: ConsigneePO.
        path: 'consignee-po',
        name: 'MOPInvConsigneePO',
        component: () => import('@/modules/Inventory/transactions/consigneePO/components/ConsigneePO.vue'),
        meta: { title: 'Raise PO based on consignee', requiresAuth: true, breadcrumb: 'Raise PO based on consignee' }
      },
      {
        // GRN creation — With PO / Without PO / Delivery Challan (Quick Create "Create a GRN"). MoceroUI: grn.
        path: 'grn-creation',
        name: 'MOPInvGrnCreation',
        component: () => import('@/modules/Inventory/transactions/grn/components/GrnCreation.vue'),
        meta: { title: 'GRN', requiresAuth: true, breadcrumb: 'GRN' }
      },
      {
        // GRN Return creation (GRN tab → GRN Return sub-tab → "+ Add New"). MoceroUI: GRNReturn.
        path: 'grn-return-creation',
        name: 'MOPInvGrnReturnCreation',
        component: () => import('@/modules/Inventory/transactions/grn/components/GrnReturnCreation.vue'),
        meta: { title: 'GRN Return', requiresAuth: true, breadcrumb: 'GRN Return' }
      },
      {
        // Item Tracker (Items tab "Item Tracker" button). MoceroUI: ItemTrackerView (/ItemTrackerView).
        path: 'item-tracker',
        name: 'MOPInvItemTracker',
        component: () => import('@/modules/Inventory/transactions/itemTracker/components/ItemTracker.vue'),
        meta: { title: 'Item Tracker', requiresAuth: true, breadcrumb: 'Item Tracker' }
      },

      {
        // Asset Opening Stock (Asset tab → Item Assets → "+ Add New" / row edit).
        // MoceroUI: OpeningStockCreation (/openingStockCreation, Asset Initial Setup).
        path: 'asset-opening-stock',
        name: 'MOPInvAssetOpeningStock',
        component: () => import('@/modules/Inventory/transactions/asset/openingStock/components/AssetOpeningStockCreation.vue'),
        meta: { title: 'Asset Opening Stock', requiresAuth: true, breadcrumb: 'Asset Opening Stock' }
      },

      {
        // Item Asset details (Asset tab → Item Assets → row click).
        // MoceroUI: ItemAssetDetails (/ItemAssetDetails) — item header + Asset/Agreement tabs.
        path: 'item-asset-details',
        name: 'MOPInvItemAssetDetails',
        component: () => import('@/modules/Inventory/transactions/asset/itemAssets/components/ItemAssetDetails.vue'),
        meta: { title: 'Item Asset Details', requiresAuth: true, breadcrumb: 'Item Asset Details' }
      },

      // --- Asset deployment quick-access pages (Asset tab → Deployment → Quick Access) ---
      {
        // Assign New Asset. MoceroUI: AssetAssign (/AssignAsset), EntityId 458.
        path: 'asset-assign',
        name: 'MOPInvAssetAssign',
        component: () => import('@/modules/Inventory/transactions/asset/deployment/components/AssetAssign.vue'),
        meta: { title: 'Assign New Asset', requiresAuth: true, breadcrumb: 'Assign New Asset' }
      },
      {
        // Bulk Assign Asset. MoceroUI: AssignBulkAsset (/AssignBulkAsset).
        path: 'asset-bulk-assign',
        name: 'MOPInvAssetBulkAssign',
        component: () => import('@/modules/Inventory/transactions/asset/deployment/components/AssetBulkAssign.vue'),
        meta: { title: 'Bulk Assign', requiresAuth: true, breadcrumb: 'Bulk Assign' }
      },
      {
        // Asset Tracking & Return Management (Asset → Dashboard "Quick Access → Asset Tracking").
        // MoceroUI: AssetTrackingandReturnManagement (/AssetTrackingReturnManagement).
        path: 'asset-tracking-return',
        name: 'MOPInvAssetTrackingReturn',
        component: () => import('@/modules/Inventory/transactions/asset/deployment/components/AssetTrackingReturn.vue'),
        meta: { title: 'Asset Tracking & Return', requiresAuth: true, breadcrumb: 'Asset Tracking & Return' }
      },
      {
        // Asset Maintenance Bulk Assign (Asset → Maintenance "Bulk Assign"). MoceroUI: AssetMaintenanceBulkAssign.
        path: 'asset-maintenance-bulk-assign',
        name: 'MOPInvAssetMaintenanceBulkAssign',
        component: () => import('@/modules/Inventory/transactions/asset/maintenance/components/AssetMaintenanceBulkAssign.vue'),
        meta: { title: 'Bulk Maintenance', requiresAuth: true, breadcrumb: 'Bulk Maintenance' }
      },

      // --- CSSD transaction creation pages (CSSD store tab → sub-tabs → Add New / Edit / View) ---
      {
        // CSSD Disposal creation (Disposal sub-tab → "+ Add New"). MoceroUI: CSSDDisposalForm, EntityId 3160.
        path: 'cssd-disposal-creation',
        name: 'MOPInvCSSDDisposalCreation',
        component: () => import('@/modules/Inventory/transactions/cssd/disposal/components/CSSDDisposalForm.vue'),
        meta: { title: 'CSSD Disposal', requiresAuth: true, breadcrumb: 'CSSD Disposal' }
      },
      {
        // CSSD Sterilization Cycle create/edit (Sterilization sub-tab). MoceroUI: SterilizationCycleForm, EntityId 3155.
        path: 'cssd-sterilization-cycle',
        name: 'MOPInvCSSDSterilizationCycle',
        component: () => import('@/modules/Inventory/transactions/cssd/sterilization/components/SterilizationCycleForm.vue'),
        meta: { title: 'Sterilization Cycle', requiresAuth: true, breadcrumb: 'Sterilization Cycle' }
      },
      {
        // CSSD Sterilization validation (Sterilization row → Validate). MoceroUI: SterilizationValidation.
        path: 'cssd-sterilization-validation',
        name: 'MOPInvCSSDSterilizationValidation',
        component: () => import('@/modules/Inventory/transactions/cssd/sterilization/components/SterilizationValidation.vue'),
        meta: { title: 'Sterilization Validation', requiresAuth: true, breadcrumb: 'Sterilization Validation' }
      },
      {
        // CSSD OT Request create/edit (OT Request sub-tab → + Add New / Edit). MoceroUI: CSSDOTRequestForm, EntityId 3158.
        path: 'cssd-ot-request-form',
        name: 'MOPInvCSSDOTRequestForm',
        component: () => import('@/modules/Inventory/transactions/cssd/otRequest/components/CSSDOTRequestForm.vue'),
        meta: { title: 'OT Request', requiresAuth: true, breadcrumb: 'OT Request' }
      },
      {
        // CSSD OT Issue creation (OT Issue outbound → "+ Add New"). MoceroUI: CSSDOTIssueForm, EntityId 3174.
        path: 'cssd-ot-issue-form',
        name: 'MOPInvCSSDOTIssueForm',
        component: () => import('@/modules/Inventory/transactions/cssd/otIssue/components/CSSDOTIssueForm.vue'),
        meta: { title: 'OT Issue', requiresAuth: true, breadcrumb: 'OT Issue' }
      },
      {
        // CSSD Kit Assembly create/edit (Kit Assembly sub-tab). MoceroUI: CSSDKitAssemblyCreation, EntityId 3156.
        path: 'cssd-kit-assembly-creation',
        name: 'MOPInvCSSDKitAssemblyCreation',
        component: () => import('@/modules/Inventory/transactions/cssd/kitAssembly/components/CSSDKitAssemblyCreation.vue'),
        meta: { title: 'CSSD Kit Assembly', requiresAuth: true, breadcrumb: 'CSSD Kit Assembly' }
      },
      {
        // CSSD Kit Assembly read-only details. MoceroUI: CSSDKitAssemblyDetails.
        path: 'cssd-kit-assembly-details',
        name: 'MOPInvCSSDKitAssemblyDetails',
        component: () => import('@/modules/Inventory/transactions/cssd/kitAssembly/components/CSSDKitAssemblyDetails.vue'),
        meta: { title: 'CSSD Kit Assembly Details', requiresAuth: true, breadcrumb: 'CSSD Kit Assembly Details' }
      },
      {
        // CSSD Kit Location & Status update form (Kit Tracking sub-tab). MoceroUI: CSSDKitTrackingForm, EntityId 3157.
        path: 'cssd-kit-tracking-form',
        name: 'MOPInvCSSDKitTrackingForm',
        component: () => import('@/modules/Inventory/transactions/cssd/kitTracking/components/CSSDKitTrackingForm.vue'),
        meta: { title: 'Kit Tracking', requiresAuth: true, breadcrumb: 'Kit Tracking' }
      },

      {
        // Stock Request create/edit (Request tab → Outbound → "Edit", or new). MoceroUI: Request (/Request, DocType 1).
        path: 'request',
        name: 'MOPInvRequest',
        component: () => import('@/modules/Inventory/transactions/request/components/RequestCreation.vue'),
        meta: { title: 'Request', requiresAuth: true, breadcrumb: 'Request' }
      },
      {
        // Indent Template create/edit (Request tab → Indent Template → "Edit"). MoceroUI: IndentTemplateCreation (DocType 21).
        path: 'indent-template',
        name: 'MOPInvIndentTemplate',
        component: () => import('@/modules/Inventory/transactions/request/components/IndentTemplateCreation.vue'),
        meta: { title: 'Indent Template', requiresAuth: true, breadcrumb: 'Indent Template' }
      },
      {
        // Stock Issue against an inbound request (Request tab → Inbound → "Issue").
        // MoceroUI: Issue (/Issue, IndirectIssue, EntityId 3048).
        path: 'issue',
        name: 'MOPInvIssue',
        component: () => import('@/modules/Inventory/transactions/request/components/IssueCreation.vue'),
        meta: { title: 'Issue', requiresAuth: true, breadcrumb: 'Issue' }
      },
      {
        // Pick List — mark request items as picked (Request tab → Inbound → "Pick List").
        // MoceroUI: PickList (/PickList, EntityId 3048).
        path: 'pick-list',
        name: 'MOPInvPickList',
        component: () => import('@/modules/Inventory/transactions/request/components/PickList.vue'),
        meta: { title: 'Pick List', requiresAuth: true, breadcrumb: 'Pick List' }
      },

      {
        // Read-only StockWO details (PO / PR / PO Amendment row click). MoceroUI: PODetails / purchaseRequestDetails / POGRNDetail.
        path: 'stockwo-details',
        name: 'MOPInvStockWODetails',
        component: () => import('@/modules/Inventory/transactions/stockwo/components/StockWODetails.vue'),
        meta: { title: 'Details', requiresAuth: true, breadcrumb: 'Details' }
      },
      {
        // Mass Asset Conversion (GRN tab → row → Convert to Asset). MoceroUI: GrnItemTableview + ScanItemRightSidebar.
        path: 'mass-asset-conversion',
        name: 'MOPInvMassAssetConversion',
        component: () => import('@/modules/Inventory/transactions/asset/massConversion/components/MassAssetConversion.vue'),
        meta: { title: 'Convert to Asset', requiresAuth: true, breadcrumb: 'Convert to Asset' }
      },
      {
        // Direct Issue creation (Item Issue tab → Outbound Issue → Add New / Edit).
        // MoceroUI: Issue.vue param "DirectIssue" (DocType 10).
        path: 'direct-issue',
        name: 'MOPInvDirectIssue',
        component: () => import('@/modules/Inventory/transactions/issue/components/DirectIssueCreation.vue'),
        meta: { title: 'Direct Issue', requiresAuth: true, breadcrumb: 'Direct Issue' }
      },
      {
        // Read-only Issue details (Item Issue tab row click). MoceroUI: IssueDetails / OutBoundIssueDetails.
        path: 'issue-details',
        name: 'MOPInvIssueDetails',
        component: () => import('@/modules/Inventory/transactions/issue/components/IssueDetails.vue'),
        meta: { title: 'Issue Details', requiresAuth: true, breadcrumb: 'Issue Details' }
      },
      {
        // Read-only GRN details (GRN tab row click). MoceroUI: GrnTableDetails.
        path: 'grn-details',
        name: 'MOPInvGrnDetails',
        component: () => import('@/modules/Inventory/transactions/grn/components/GrnDetails.vue'),
        meta: { title: 'GRN Details', requiresAuth: true, breadcrumb: 'GRN Details' }
      },

      // --- Navbar action pages ---
      {
        path: 'code-template',
        name: 'MOPInvCodeTemplate',
        component: () => import('@/modules/Inventory/settings/components/CodeTemplatePage.vue'),
        meta: { title: 'Code Template', requiresAuth: true, breadcrumb: 'Code Template' }
      },
      {
        // Reuse the user-management settings screen (old MoceroUI /userMaster) UNCHANGED,
        // but nested under the inventory layout so it renders with the inventory navbar.
        // UserManagementLayout supplies its own settings sidebar + <router-view>.
        path: 'settings',
        component: () => import('@/modules/UserManagement/layout/UserManagementLayout.vue'),
        meta: { title: 'Settings', requiresAuth: true, breadcrumb: 'Settings' },
        children: [
          {
            path: '',
            name: 'MOPInvSettings',
            component: () => import('@/modules/UserManagement/masters/UserManagementMaster.vue'),
            meta: { title: 'Settings', requiresAuth: true, breadcrumb: 'Settings' }
          }
        ]
      },
      {
        // Work List → entity-wise approval table. Inventory-specific page that mirrors the
        // MoceroUI /entitywiseApprovalTableView layout (filter sidebar + approval-history
        // table/empty state), mounted here so it keeps the inventory navbar.
        path: 'approval/entitywise',
        name: 'MOPInvApprovalEntitywise',
        component: () => import('@/modules/Inventory/approval/components/InventoryApprovalView.vue'),
        meta: { title: 'Approval History', requiresAuth: true, breadcrumb: 'Approval History' }
      }
    ]
  }
]
